/**
 * 产品特性Schema解析器
 * 用于解析从后端获取的feature_schema并生成表单字段配置
 */

/**
 * 解析产品类型的feature_schema
 * @param {Object} featureSchema - 从API获取的feature_schema对象
 * @returns {Array} 解析后的字段配置数组
 */
export const parseFeatureSchema = (featureSchema) => {
    if (!featureSchema) return [];

    const fields = [];

    // 解析账号级别配额
    if (featureSchema.account_level) {
        Object.entries(featureSchema.account_level).forEach(([key, config]) => {
            fields.push({
                key,
                label: config.display || key,
                type: config.type,
                values: config.values,
                unit: config.unit,
                quotaType: config.quota_type,
                level: 'account',
                description: config.description,
                readonly: config.description?.includes('仅管理员') || false
            });
        });
    }

    // 解析资源级别配额
    if (featureSchema.resource_level) {
        Object.entries(featureSchema.resource_level).forEach(([key, config]) => {
            fields.push({
                key,
                label: config.display || key,
                type: config.type,
                values: config.values,
                unit: config.unit,
                quotaType: config.quota_type,
                level: 'resource',
                appliesTo: config.applies_to,
                description: config.description,
                readonly: false
            });
        });
    }

    return fields;
};

/**
 * 根据字段类型生成默认值
 * @param {Object} fieldConfig - 字段配置
 * @returns {*} 默认值
 */
export const getDefaultValue = (fieldConfig) => {
    switch (fieldConfig.type) {
        case 'boolean':
            return false;
        case 'integer':
            return 0;
        case 'enum':
            return fieldConfig.values?.[0] || '';
        default:
            return null;
    }
};

/**
 * 生成产品特性模板（从schema）
 * @param {Object} featureSchema - feature_schema对象
 * @returns {Object} 特性模板对象
 */
export const generateFeatureTemplate = (featureSchema) => {
    const template = {};
    const fields = parseFeatureSchema(featureSchema);

    fields.forEach(field => {
        template[field.key] = getDefaultValue(field);
    });

    return template;
};

/**
 * 验证特性值是否符合schema定义
 * @param {Object} features - 特性值对象
 * @param {Object} featureSchema - feature_schema对象
 * @returns {Object} { valid: boolean, errors: Array }
 */
export const validateFeaturesAgainstSchema = (features, featureSchema) => {
    const errors = [];
    const fields = parseFeatureSchema(featureSchema);

    fields.forEach(field => {
        const value = features[field.key];

        // 跳过未设置的可选字段
        if (value === undefined || value === null) {
            return;
        }

        // 类型验证
        if (field.type === 'integer' && !Number.isInteger(value)) {
            errors.push(`${field.label} 必须是整数`);
        } else if (field.type === 'boolean' && typeof value !== 'boolean') {
            errors.push(`${field.label} 必须是布尔值`);
        } else if (field.type === 'enum' && field.values && !field.values.includes(value)) {
            errors.push(`${field.label} 必须是以下值之一: ${field.values.join(', ')}`);
        }

        // 数字范围验证
        if (field.type === 'integer' && value < 0) {
            errors.push(`${field.label} 不能为负数`);
        }
    });

    return {
        valid: errors.length === 0,
        errors
    };
};

/**
 * 格式化特性值用于显示
 * @param {string} key - 特性键
 * @param {*} value - 特性值
 * @param {Object} fieldConfig - 字段配置
 * @returns {string} 格式化后的显示文本
 */
export const formatFeatureValue = (key, value, fieldConfig) => {
    if (value === undefined || value === null) {
        return '-';
    }

    switch (fieldConfig.type) {
        case 'boolean':
            return value ? '是' : '否';
        case 'integer':
            return fieldConfig.unit ? `${value} ${fieldConfig.unit}` : value.toString();
        case 'enum':
            // 可以在这里添加枚举值的中文映射
            return value;
        default:
            return value.toString();
    }
};

export default {
    parseFeatureSchema,
    getDefaultValue,
    generateFeatureTemplate,
    validateFeaturesAgainstSchema,
    formatFeatureValue
};
